import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpHeaders,
  HttpRequest,
  HttpResponse,
} from "@angular/common/http";
import { inject, InjectionToken, Provider } from "@angular/core";
import { Environment } from "@kalmarenergi/util/models";
import { Observable, throwError, timer } from "rxjs";
import { concatMap } from "rxjs/operators";
import { APP_ENVIRONMENT } from "../../config/config";

/**
 * Mocked HTTP response shape returned by a request handler.
 *
 * Defaults when omitted:
 * - status: 200
 * - statusText: "OK" for 2xx, "Error" for non-2xx
 * - body: null
 * - delayMs: environment.mockApi.delayMs or 0
 */
export interface MockApiResponse {
  body?: unknown;
  status?: number;
  statusText?: string;
  headers?: HttpHeaders | Record<string, string | string[]>;
  delayMs?: number;
}

/**
 * Contract for mocking an endpoint.
 *
 * Handlers are evaluated in registration order and the first match is used.
 */
export interface MockApiRequestHandler {
  matches(request: HttpRequest<unknown>, environment: Environment): boolean;
  respond(
    request: HttpRequest<unknown>,
    environment: Environment,
  ): MockApiResponse;
}

export const MOCK_API_HANDLERS = new InjectionToken<MockApiRequestHandler[]>(
  "Mock API handlers",
);

/**
 * Registers mock API handlers as multi-providers for MOCK_API_HANDLERS.
 */
export function provideMockApiHandlers(
  handlers: MockApiRequestHandler[],
): Provider[] {
  return handlers.map((handler) => ({
    provide: MOCK_API_HANDLERS,
    useValue: handler,
    multi: true,
  }));
}

/**
 * Intercepts HTTP requests when mock mode is enabled and returns mocked responses.
 *
 * Behavior:
 * - mockApi disabled: forwards request unchanged.
 * - no matching handler: forwards request unchanged.
 * - matching handler: returns mocked response after optional delay.
 */
export function mockApiInterceptor(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  const environment = inject(APP_ENVIRONMENT);

  if (!environment.mockApi?.enabled) {
    return next(request);
  }

  const handlers = inject(MOCK_API_HANDLERS, { optional: true }) ?? [];
  const handler = handlers.find((candidate) =>
    candidate.matches(request, environment),
  );

  if (!handler) {
    return next(request);
  }

  const response = handler.respond(request, environment);
  const delayMs = response.delayMs ?? environment.mockApi.delayMs ?? 0;
  const headers =
    response.headers instanceof HttpHeaders
      ? response.headers
      : new HttpHeaders(response.headers);
  const status = response.status ?? 200;
  const isErrorStatus = status < 200 || status >= 300;

  return timer(delayMs).pipe(
    concatMap(() => {
      if (isErrorStatus) {
        return throwError(
          () =>
            new HttpErrorResponse({
              error: response.body ?? null,
              headers,
              status,
              statusText: response.statusText ?? "Error",
              url: request.url,
            }),
        );
      }

      return [
        new HttpResponse({
          body: response.body ?? null,
          headers,
          status,
          statusText: response.statusText ?? "OK",
          url: request.url,
        }),
      ];
    }),
  );
}
